import { colord } from 'colord';
import json5 from 'json5';

export const getUserProperties = (data) => {
  const curlyBracedRegexp = /@{(.*?)}/g;
  const groupKeysMayContainProperty = ['icon_image_url', 'title'];
  const properties = [];

  data.forEach((group) => {
    const processUserProperty = (content, propertyType) => {
      const matchedProperty = content[propertyType]?.match(curlyBracedRegexp);
      if (matchedProperty?.length) {
        const propWithoutBraces = matchedProperty[0].replace(
          curlyBracedRegexp,
          (_, capturedContent) => capturedContent
        );

        properties.push({
          label: propWithoutBraces,
          value: matchedProperty[0],
          key: matchedProperty[0],
        });
      }
    };

    // properties for title and icon
    const propertyLength = groupKeysMayContainProperty.length;
    for (let i = 0; i < propertyLength; i += 1) {
      const key = groupKeysMayContainProperty[i];
      processUserProperty(group, key);
    }

    const { stories } = group;

    //  // properties for layers
    const storyLength = stories.length;
    for (let s = 0; s < storyLength; s += 1) {
      const { layers } = stories[s].media;

      if (stories[s].title) {
        processUserProperty(stories[s], 'title');
      }
      if (stories[s].alt_text) {
        processUserProperty(stories[s], 'alt_text');
      }

      const layerLength = layers.length;
      for (let l = 0; l < layerLength; l += 1) {
        const layer = layers[l];
        const matchedKeys = Object.keys(layer).filter((k) => {
          if (typeof layer[k] === 'string') {
            return !!layer[k].match(curlyBracedRegexp);
          }
          return false;
        });

        const keyLength = matchedKeys.length;
        for (let k = 0; k < keyLength; k += 1) {
          const matchedKey = matchedKeys[k];
          processUserProperty(layer, matchedKey);
        }
      }
    }
  });

  return [...new Set(properties.map((o) => JSON.stringify(o)))].map((s) =>
    JSON.parse(s)
  );
};

export const decrypt = (salt, encoded) => {
  if (!encoded) return;
  const textToChars = (text) => text.split('').map((c) => c.charCodeAt(0));
  const applySaltToChar = (code) =>
    // eslint-disable-next-line no-bitwise
    textToChars(salt).reduce((a, b) => a ^ b, code);
  return encoded
    .match(/.{1,2}/g)
    .map((hex) => parseInt(hex, 16))
    .map(applySaltToChar)
    .map((charCode) => String.fromCharCode(charCode))
    .join('');
};

export const handleInitilazionData = (data) => {
  return {
    ...data,
    productFeed: data.productFeed
      ? json5.parse(data.productFeed.replace(';', ''))
      : null,
    barLayout: {
      ...(data.barLayout === 'energized'
        ? {
            view: 'energized',
            style: {
              height: 360,
            },
          }
        : {}),
    },
    // props: {
    //   ...(data.props || {}),
    //   scrollIndicator: data.props.scrollIndicator === 'on' ? true : false,
    //   storyGroupSize: `${data.props.storyGroupSizeWidth}x${data.props.storyGroupSizeHeight}`,
    //   storyGroupBorderSize: +data.props.storyGroupBorderSize,
    //   storyGroupAnimation: data.props.storyGroupAnimation
    //     ? {
    //         type: 'pulse',
    //       }
    //     : false,
    // },
  };
};

let defaultInitalProducts = {
  products: [],
};

export const handleStoryProductAdded = (data) => {
  const { product, onSuccess } = data;
  const filteredProducts = defaultInitalProducts.products.filter(
    (p) => p.product_id !== product.product_id
  );
  if (product.quantity) filteredProducts.push(product);

  const prices = [].concat(filteredProducts).reduce(
    (acc, val) => {
      const newVal = {
        price: acc.price + val.price * val.quantity,
        sales_price: acc.sales_price + val.sales_price * val.quantity,
      };
      return newVal;
    },
    {
      price: 0,
      sales_price: 0,
    }
  );

  const payload = {
    products: filteredProducts,
    price: prices.price,
    sales_price: prices.sales_price,
    price_currency: 'USD',
  };
  defaultInitalProducts = payload;
  onSuccess(payload);
};

export const rebuildStorylyDOM = (storylyRef, storylyContainerRef) => {
  storylyRef.current?.remove();
  const storylyTag = document.createElement('storyly-web');
  storylyContainerRef.current.appendChild(storylyTag);
  storylyRef.current = storylyTag;
};

export const createDefaultPositions = (colorsArray) => {
  const startColor = colorsArray[0];
  const endColor = colorsArray[colorsArray.length - 1];
  const lastStartColorIndex = colorsArray.lastIndexOf(startColor);
  const endColorIndex = colorsArray.findIndex((color) => color === endColor);
  if (lastStartColorIndex === -1 || endColorIndex === -1) {
    return [0, 100];
  }
  if (colorsArray.every((color) => color === startColor)) {
    return [0, 100];
  }
  return [
    lastStartColorIndex === 0
      ? 0
      : ((lastStartColorIndex + 1) / colorsArray.length) * 100,
    endColorIndex === colorsArray.length - 1
      ? 100
      : (endColorIndex / colorsArray.length) * 100,
  ];
};

export const gcd = (numbers) => {
  function findGCD(a, b) {
    if (b === 0) {
      return a;
    }
    return findGCD(b, a % b);
  }
  function findMultipleGCD(numbers) {
    let result = numbers[0];
    for (let i = 1; i < numbers.length; i++) {
      result = findGCD(result, numbers[i]);
    }
    return result;
  }
  return findMultipleGCD(numbers);
};

export const interpolateColor = (color1, color2, ratio) => {
  const result = {
    r: Math.round(color1.r + (color2.r - color1.r) * ratio),
    g: Math.round(color1.g + (color2.g - color1.g) * ratio),
    b: Math.round(color1.b + (color2.b - color1.b) * ratio),
    a: Math.round(color1.a + (color2.a - color1.a) * ratio),
  };
  return `rgba(${result.r}, ${result.g}, ${result.b}, ${result.a})`;
};

export const getColorsPalette = (
  startColor,
  endColor,
  startPosition, // percentage (0-100)
  endPosition // percentage (0-100)
) => {
  const maxArraySize = 10; // array size for gradient colors;
  const allowedError = Math.floor(100 / maxArraySize);
  const startInterval = startPosition;
  const endInterval = 100 - endPosition;
  if (startInterval < allowedError && endInterval < allowedError)
    return [startColor, endColor];
  let startColorCount =
    startInterval < allowedError
      ? 1
      : startInterval !== 45 && startInterval !== 55
        ? Math.round(startInterval / maxArraySize)
        : Math.floor(startInterval / maxArraySize);
  let endColorCount =
    endInterval < allowedError ? 1 : Math.round(endInterval / maxArraySize);
  let intervalColorCount = maxArraySize - (startColorCount + endColorCount);
  // try to reduce the number of colors by finding the greatest common divisor
  const greatestCommonDivisor = gcd([
    startColorCount,
    endColorCount,
    intervalColorCount,
  ]);
  if (
    greatestCommonDivisor > 1 &&
    startColorCount / greatestCommonDivisor !==
      endColorCount / greatestCommonDivisor &&
    startColorCount / greatestCommonDivisor !==
      intervalColorCount / greatestCommonDivisor &&
    endColorCount / greatestCommonDivisor !==
      intervalColorCount / greatestCommonDivisor
  ) {
    startColorCount /= greatestCommonDivisor;
    endColorCount /= greatestCommonDivisor;
    intervalColorCount /= greatestCommonDivisor;
  }
  // create the array of colors
  const result = [];
  for (let i = 0; i < startColorCount; i++) {
    result.push(startColor);
  }
  for (let i = 0; i < intervalColorCount; i++) {
    const ratio = (i + 1) / (intervalColorCount + 1);
    result.push(
      interpolateColor(
        colord(startColor).toRgb(),
        colord(endColor).toRgb(),
        ratio
      )
    );
  }
  for (let i = 0; i < endColorCount; i++) {
    result.push(endColor);
  }

  return result;
};

export const rgbToRgba = (color, alpha = 1) => {
  if (color.substring(0, 3) !== 'rgb') {
    return '';
  }
  if (color.substring(0, 4) === 'rgba') {
    return color;
  }
  const rgx = /^rgb\(((,?\s*\d+){3}).+$/;
  return color.replace(rgx, `rgba($1, ${alpha})`);
};

export const hexToRgba = (hex, a) => {
  const hashlessHex = removeHash(hex);
  const hexObject = parseHex(hashlessHex);
  const decimalObject = hexesToDecimals(hexObject);

  return formatRgb(decimalObject, a);
};

export const isNumeric = (n) => !isNaN(parseFloat(n)) && isFinite(n);

export const colorArrayToLinearGradient = (colors, deg = 180) => {
  if (colors.length === 2) {
    return `linear-gradient(${deg}deg, ${colors[0]} 0%, ${
      colors[colors.length - 1]
    } 100%)`;
  }
  // flat color if they are at the same position:
  const colorSet = new Set(colors);
  if (colorSet.size <= 2) {
    const lastIndex = colors.lastIndexOf(colors[0]);
    const percentage = ((lastIndex + 1) / colors.length) * 100;
    return `linear-gradient(${deg}deg, ${colors[0]} ${percentage}%, ${
      colors[colors.length - 1]
    } ${percentage}%)`;
  }
  // linear gradient if they are at different positions:
  const linearGradient = colors
    .map((color, index) => {
      const percentage = index === 0 ? 0 : ((index + 1) / colors.length) * 100;
      return `${color} ${percentage}%`;
    })
    .join(', ');
  return `linear-gradient(${deg}deg, ${linearGradient})`;
};
